import type { TFunction } from "i18next";
import { useState, useRef, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { U2000LUStateEnumNextAction } from "../../../../../u2000Core/reducer/formReducer/U2000LUState";
import type { NavigateFunction } from "react-router-dom";
import type { U2104Affiliation, U2104User } from "../../../../U2104Service";

// Gestion des handlers pour les actions d'une unité logique
export interface U2104OrganizationrAffiliationHandler {
  onRowClick: (user: U2104User) => void;
  onImport: () => void;
  onAdd: () => void;
  onModify: () => void;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104Affiliation>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U2104OrganizationAffiliationHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104Affiliation>>;
  t: TFunction;
  organizationId: string;
  navigate: NavigateFunction;
}

// Gestion des handlers pour les actions d'une unité logique
export interface U2104OrganizationAffiliationHandlerReturn {
  onRowClick: (user: U2104Affiliation) => void;
  onImport: () => void;
  onAdd: () => void;
  onModify: () => void;

  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U2104Affiliation>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les actions d'une unité logique
export function u2104OrganizationAffiliationHandler(params: U2104OrganizationAffiliationHandlerParam): U2104OrganizationAffiliationHandlerReturn {
  const { luStateHandlers, gridStateHandlers, navigate } = params;
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [localEditMode, _setLocalEditMode] = useState<U2000FormStateEnumEditMode>(U2000FormStateEnumEditMode.NONE);
  const localEditModeRef = useRef(localEditMode);

  useEffect(() => {
    localEditModeRef.current = localEditMode;
  }, [localEditMode]);

  const onRowClick = (user: U2104Affiliation) => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
    luStateHandlers.setActionOriginFrom(`/U2104/AFFILIATION`);
    const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === user.id) : -1;
    if (gridStateHandlers) {
      gridStateHandlers.setCurrentIdx(currentIdx);
    }
    navigate(`affiliation/${user.id}`);
  };

  // Gestion de l'importation d'utilisateurs affiliés
  const onImport = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    luStateHandlers.setActionOriginFrom(`/U2104/ORGANIZATION`);
    navigate("affiliation/import");
  };

  // Gestion de l'ajout d'une action d'unité logique
  const onAdd = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    luStateHandlers.setActionOriginFrom(`/U2104/ORGANIZATION`);
    navigate("affiliation/new");
  };

  // Gestion de la modification d'une action d'unité logique
  const onModify = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
    luStateHandlers.setActionOriginFrom(`/U2104/ORGANIZATION`);
    navigate(`affiliation/${gridStateHandlers.getSelectedRows()[0]?.id}`);
  };

  return {
    onImport,
    onRowClick,
    onAdd,
    onModify,

    gridStateHandlers,
    setDataChangedEvent,
    dataChangedEvent,
  };
}
